import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert, Badge, Box, Button, Drawer, IconButton, Link, Stack, Step, StepButton, Stepper,
  Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import AddIcon from '@mui/icons-material/Add';
import StudioV2AppRuntimeShell from '../studio/StudioV2AppRuntimeShell';
import CitizenOutcomeTray from './CitizenOutcomeTray';
import { useDashboard } from '../../contexts/DashboardContext';
import { normalizeInterviewPhases } from '../../utils/interviewFlowRuntime';
import { citizenNewRequestSearch } from '../../utils/citizenNewRequest';
import {
  historyEntryFromAdvise,
  rememberCitizenRequest,
} from '../../utils/citizenRequestHistory';
import { getDiscoverySessionId, peekDiscoverySessionId } from '../../utils/discoverySession';
import CitizenRequestHistory from './CitizenRequestHistory';
import DemoSiteNav from '../demo/DemoSiteNav';
import {
  IF_BRAND,
  ensureInterviewFlowFonts,
  interviewFlowCssVars,
} from '../../theme/interviewFlowBrand';

function citizenSubtitle(portalVariant) {
  const raw = portalVariant?.subtitle || '';
  if (!raw || /CLAW|Studio/i.test(raw)) {
    return 'Find the right application path — guidance only, not an official determination.';
  }
  return raw;
}

function cityDisplayName(pkg, portalVariant) {
  const fromPkg = pkg?.name || pkg?.catalog?.name;
  if (fromPkg && !/^san-francisco$/i.test(String(fromPkg))) return fromPkg;
  if (fromPkg) return 'City and County of San Francisco';
  if (portalVariant?.jurisdictionName) return portalVariant.jurisdictionName;
  return 'City application advisor';
}

function CitizenChrome({
  cityName,
  portalVariant,
  portalLinks = [],
  showPathButton = false,
  onOpenPath = null,
  catalogOnly = false,
  geocodeOnly = false,
  catalogDisclaimer = null,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isCompact = useMediaQuery('(max-height:820px), (max-width:1100px)');
  const {
    config, interviewPhaseId, setInterviewPhaseId, structuredOutput, panelOutputs,
    startNewCitizenRequest,
  } = useDashboard();

  const phases = useMemo(
    () => normalizeInterviewPhases(config?.metadata?.interviewFlow?.phases),
    [config?.metadata?.interviewFlow?.phases],
  );
  const activeId = interviewPhaseId
    || config?.metadata?.interviewFlow?.initialPhaseId
    || phases[0]?.id;
  const activeStep = Math.max(0, phases.findIndex((p) => p.id === activeId));
  const subtitle = citizenSubtitle(portalVariant) === 'Find the right application path — guidance only, not an official determination.'
    ? t('citizen.subtitle')
    : citizenSubtitle(portalVariant);

  const hasApplicationsData = Boolean(
    structuredOutput?.routing?.primaryTrack
    || structuredOutput?.routing?.primaryTrackLabel
    || structuredOutput?.table_data?.rows?.length
    || structuredOutput?.checklist?.rows?.length
    || panelOutputs?.left_checklist?.table_data?.rows?.length,
  );
  const hasPathResult = Boolean(
    structuredOutput?.routing?.primaryTrack
    || structuredOutput?.routing?.primaryTrackLabel
    || panelOutputs?.left_checklist?.routing?.primaryTrack,
  );
  const playbook = String(config?.metadata?.interviewFlow?.playbook || '');
  const isIntakePlaybook = playbook === 'permits-intake' || playbook === 'application-intake';
  const intakeReady = Boolean(
    structuredOutput?.gaps?.ready
    || structuredOutput?.routing?.gaps?.ready
    || structuredOutput?.routing?.publicTrackingCode
    || structuredOutput?.session?.publicTrackingCode,
  );
  const intakeReceipt = Boolean(
    structuredOutput?.routing?.publicTrackingCode
    || structuredOutput?.session?.publicTrackingCode,
  );
  const canSelectPhase = (phaseId) => {
    if (phaseId === 'advise' || phaseId === 'intake' || !phaseId) return true;
    if (phaseId === 'checklist' || phaseId === 'next-steps') return hasApplicationsData;
    if (isIntakePlaybook && phaseId === 'ready') return intakeReady;
    if (isIntakePlaybook && phaseId === 'receipt') return intakeReceipt;
    return true;
  };

  return (
    <Box
      sx={{
        flexShrink: 0,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        zIndex: 2,
      }}
      data-testid="citizen-advisor-chrome"
    >
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          pt: isCompact ? 1.25 : 1.75,
          pb: isCompact ? 1 : 1.25,
          background: IF_BRAND.headerGradient,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          justifyContent="space-between"
          useFlexGap
          flexWrap="wrap"
        >
          <Box sx={{ minWidth: 0, flex: '1 1 220px' }}>
            <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
              <Typography
                component="h1"
                sx={{
                  fontFamily: IF_BRAND.display,
                  fontWeight: 700,
                  fontSize: isCompact ? { xs: '1.15rem', sm: '1.35rem' } : { xs: '1.35rem', sm: '1.6rem' },
                  letterSpacing: '-0.03em',
                  lineHeight: 1.15,
                  color: 'var(--citizen-primary, #1976d2)',
                }}
              >
                {cityName}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                {t('citizen.guidanceOnly')}
                {' · '}
                <Link component={RouterLink} to="/help/permits" underline="hover" color="inherit">
                  {t('citizen.howThisWorks')}
                </Link>
              </Typography>
            </Stack>
            {!isCompact && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.4, maxWidth: 520, lineHeight: 1.35 }}
              >
                {portalVariant?.title ? (
                  <>
                    <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {portalVariant.title}
                    </Box>
                    {' — '}
                    {subtitle}
                  </>
                ) : subtitle}
              </Typography>
            )}
          </Box>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ flexShrink: 0, maxWidth: '100%' }}
          >
            <DemoSiteNav />
            <CitizenRequestHistory
              packageId={config?.metadata?.interviewFlow?.jurisdictionId || null}
              refreshToken={structuredOutput?.routing?.primaryTrack || structuredOutput?.updatedAt || 0}
            />
            {typeof startNewCitizenRequest === 'function' && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  const pkgId = config?.metadata?.interviewFlow?.jurisdictionId || null;
                  const entry = historyEntryFromAdvise({
                    structuredOutput,
                    packageId: pkgId,
                    sessionId: peekDiscoverySessionId() || getDiscoverySessionId(),
                  });
                  if (entry) rememberCitizenRequest(entry);
                  startNewCitizenRequest();
                  navigate({ search: citizenNewRequestSearch(searchParams) }, { replace: true });
                }}
                sx={{
                  textTransform: 'none',
                  minHeight: 30,
                  px: 1.1,
                  borderColor: 'divider',
                  color: 'text.secondary',
                }}
                data-testid="citizen-new-request-header"
              >
                New request
              </Button>
            )}
            {showPathButton && typeof onOpenPath === 'function' && (
              <Tooltip title="Your next path">
                <IconButton
                  onClick={onOpenPath}
                  aria-label="Open your next path"
                  data-testid="citizen-path-panel-button"
                  size="small"
                  sx={{
                    color: 'var(--citizen-primary, #1976d2)',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    width: 36,
                    height: 36,
                  }}
                >
                  <Badge
                    color="primary"
                    variant="dot"
                    invisible={!hasPathResult}
                    overlap="circular"
                  >
                    <ViewSidebarOutlinedIcon fontSize="small" />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
            {portalLinks.map((p) => (
              <Button
                key={p.to}
                component={RouterLink}
                to={p.to}
                size="small"
                variant={p.active ? 'contained' : 'outlined'}
                sx={{
                  textTransform: 'none',
                  minHeight: 30,
                  px: 1.1,
                  borderColor: 'divider',
                  fontFamily: IF_BRAND.body,
                  ...(p.active ? {
                    bgcolor: 'var(--citizen-primary, #1976d2)',
                    borderColor: 'var(--citizen-primary, #1976d2)',
                    '&:hover': { bgcolor: 'var(--citizen-primary, #1976d2)', filter: 'brightness(0.92)' },
                  } : {
                    color: 'text.secondary',
                  }),
                }}
              >
                {p.label}
              </Button>
            ))}
          </Stack>
        </Stack>
      </Box>

      {(catalogOnly || geocodeOnly) && (
        <Alert
          severity="info"
          sx={{
            borderRadius: 0,
            py: 0.35,
            '& .MuiAlert-message': { py: 0.25, fontSize: '0.8125rem' },
          }}
          data-testid="citizen-catalog-only-banner"
        >
          {catalogDisclaimer
            || (geocodeOnly
              ? 'We can place your address on the map; zoning details use this city’s published rules.'
              : t('citizen.catalogOnlyBanner'))}
        </Alert>
      )}

      {phases.length > 0 && (
        <Box sx={{ px: { xs: 0.5, sm: 1.5 }, py: isCompact ? 0.75 : 1 }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel={false}
            orientation="horizontal"
            nonLinear
            sx={{
              '& .MuiStepConnector-root': { top: 12 },
              '& .MuiStepLabel-root': { alignItems: 'center' },
              '& .MuiStepLabel-label': {
                fontFamily: IF_BRAND.body,
                fontSize: isCompact ? '0.72rem' : '0.8rem',
                fontWeight: 600,
                mt: '0 !important',
                ml: 0.75,
              },
              '& .MuiStepLabel-iconContainer': { pr: 0 },
              '& .MuiStepIcon-root': { width: isCompact ? 22 : 24, height: isCompact ? 22 : 24 },
              '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': {
                color: 'var(--citizen-primary, #1976d2)',
              },
              '& .MuiStepConnector-line': { borderColor: 'divider' },
            }}
          >
            {phases.map((ph) => (
              <Step
                key={ph.id}
                completed={phases.findIndex((p) => p.id === ph.id) < activeStep}
                disabled={!canSelectPhase(ph.id)}
              >
                <StepButton
                  onClick={() => {
                    if (!canSelectPhase(ph.id)) return;
                    setInterviewPhaseId(ph.id);
                  }}
                  disabled={!canSelectPhase(ph.id)}
                  sx={{
                    textTransform: 'none',
                    py: 0.25,
                    '& .MuiStepLabel-horizontal': {
                      flexDirection: 'row',
                      gap: 0,
                    },
                  }}
                >
                  {String(ph.label || ph.id).replace(/^\d+\s*·\s*/, '')}
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </Box>
      )}
    </Box>
  );
}

function CitizenPathDrawer({ open, onClose, officialApplyUrl }) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      data-testid="citizen-path-drawer"
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: 'min(100vw, 360px)',
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: '#fafbfc',
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontFamily: IF_BRAND.display,
            fontWeight: 700,
            color: 'var(--citizen-primary, #1976d2)',
          }}
        >
          Your next path
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close path panel"
          data-testid="citizen-path-drawer-close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
      >
        <CitizenOutcomeTray
          variant="drawer"
          officialApplyUrl={officialApplyUrl}
          onRequestClose={onClose}
        />
      </Box>
    </Drawer>
  );
}

/**
 * Citizen-first shell around the Studio V2 app runtime.
 */
export default function CitizenAdvisorShell({
  definition,
  jurisdictionPackage = null,
  portalVariant = null,
  mongoId = null,
  portalLinks = [],
  darkMode = false,
  footer = null,
}) {
  const theme = useTheme();
  const isSff = useMediaQuery(theme.breakpoints.down('md'));
  const [pathOpen, setPathOpen] = useState(false);
  const primaryColor = portalVariant?.theme?.primaryColor
    || definition?.theme?.primaryColor
    || IF_BRAND.accent;
  const cityName = cityDisplayName(jurisdictionPackage, portalVariant);
  const officialApplyUrl = jurisdictionPackage?.catalog?.officialApplyUrl
    || jurisdictionPackage?.officialApplyUrl
    || null;
  const parcelAdapter = jurisdictionPackage?.parcelAdapter
    || jurisdictionPackage?.intake?.parcelAdapter
    || definition?.metadata?.interviewFlow?.parcelAdapter
    || 'none';
  const catalogOnly = parcelAdapter === 'none' || !parcelAdapter;
  const geocodeOnly = parcelAdapter === 'arcgis-geocode';
  const catalogDisclaimer = jurisdictionPackage?.catalog?.disclaimer
    || jurisdictionPackage?.disclaimer
    || null;

  useEffect(() => { ensureInterviewFlowFonts(); }, []);

  // Close the SFF properties drawer when switching to desktop rail.
  useEffect(() => {
    if (!isSff) setPathOpen(false);
  }, [isSff]);

  const citizenDefinition = useMemo(() => {
    if (!definition) return null;
    const iflow = definition.metadata?.interviewFlow || {};
    const isIntake = iflow.playbook === 'permits-intake' || iflow.playbook === 'application-intake';
    const title = portalVariant?.title
      || (isIntake
        ? `${cityName.replace(/^City and County of\s+/i, '')} Application intake`
        : `${cityName.replace(/^City and County of\s+/i, '')} Application Advisor`);
    return {
      ...definition,
      name: title,
      theme: {
        ...(definition.theme || {}),
        ...(portalVariant?.theme || {}),
        primaryColor,
        headerStyle: 'flat',
        themeId: portalVariant?.theme?.themeId || definition.theme?.themeId || 'ocean',
      },
      metadata: {
        ...(definition.metadata || {}),
        interviewFlow: {
          ...iflow,
          citizenMode: true,
          // Intake keeps catalog advise HTTP path but server may run agent tool loop.
          // Advisor demos always use publicAdvise catalog path.
          publicAdvise: isIntake
            ? (iflow.publicAdvise !== false)
            : true,
          liveSkillLoop: isIntake ? (iflow.liveSkillLoop !== false) : Boolean(iflow.liveSkillLoop),
          parcelAdapter: iflow.parcelAdapter || parcelAdapter,
          helpPath: iflow.helpPath || '/help/permits',
          intakeGates: {
            requireAddress: isIntake ? false : true,
            requirePropertyType: isIntake ? false : true,
            minAddressLength: isIntake ? 0 : 8,
            ...(iflow.intakeGates || {}),
          },
        },
      },
    };
  }, [definition, portalVariant, cityName, primaryColor, parcelAdapter]);

  if (!citizenDefinition) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        maxHeight: '100dvh',
        overflow: 'hidden',
        bgcolor: IF_BRAND.canvas,
        backgroundImage: IF_BRAND.shellWash,
        ...interviewFlowCssVars(primaryColor),
        fontFamily: IF_BRAND.body,
      }}
      data-testid="citizen-advisor-shell"
    >
      <StudioV2AppRuntimeShell
        definition={citizenDefinition}
        mongoId={mongoId || citizenDefinition.id}
        studioPreview
        demoReadOnly
        citizenMode
        hideAppHeader
        darkMode={darkMode}
        height="100%"
        chrome={(
          <>
            <CitizenChrome
              cityName={cityName}
              portalVariant={portalVariant}
              portalLinks={portalLinks}
              showPathButton={isSff}
              onOpenPath={() => setPathOpen(true)}
              catalogOnly={catalogOnly}
              geocodeOnly={geocodeOnly}
              catalogDisclaimer={
                catalogOnly
                  ? (catalogDisclaimer
                    ? `${catalogDisclaimer} Guidance uses this city’s published rules (not live parcel GIS).`
                    : null)
                  : (geocodeOnly
                    ? 'We validate the address pin; zoning guidance uses this city’s published rules.'
                    : null)
              }
            />
            {isSff && (
              <CitizenPathDrawer
                open={pathOpen}
                onClose={() => setPathOpen(false)}
                officialApplyUrl={officialApplyUrl}
              />
            )}
          </>
        )}
        aside={isSff ? null : (
          <CitizenOutcomeTray officialApplyUrl={officialApplyUrl} />
        )}
        footer={footer}
      />
    </Box>
  );
}
